'use client';

import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommerceSource, useBlockPart, PortalLink } from '@akademi/site-builder-runtime';
import {
  EmptyState,
  SkeletonRow,
  AdapterPlaceholder,
  useAsyncResource,
  BlockRoot,
  sharedStyles,
} from '@akademi/site-builder-blocks';
import { ProductThumb } from './ProductThumb';
import { ProductPrice } from './ProductPrice';
import { AvailabilityBadge } from './AvailabilityBadge';
import { SavedListPicker, hasBuyingListsCapability, type SavedListPickerSource } from './SavedListPicker';

const { sectionInner, sectionPad } = sharedStyles;

/**
 * Renders a single {@code ProductDetail} — reads through
 * {@code useCommerceSource()} like every other host-coupled block. Never
 * fetches directly, never throws, and shows an inline placeholder for both
 * "no provider mounted" and "product not found" (a moved/missing slug is a
 * normal outcome on a public page, not an error).
 *
 * <p>Purely informational: image gallery, title, price, availability,
 * description, spec attributes, downloadable documents. It carries NO
 * commit action of its own (no "add to basket" — the platform's kits close
 * on a phone call, not through a cart) — drop a {@code KitEnquiryCta} block
 * alongside it for the CTA.
 *
 * <h3>Slug resolution</h3>
 * Prefers the explicit {@code slug} prop; falls back to the last path
 * segment of the current URL so the block can sit on a {@code /p/{slug}} or
 * {@code /kits/{slug}} page template without per-page configuration. Never
 * builds a link from that slug itself elsewhere on the page — every link
 * this block renders uses the fetched {@code publicPath} / document URLs
 * verbatim.
 */
export interface ProductDetailProps {
  /** Explicit slug. When unset, falls back to the URL's last path segment. */
  slug?: string;
  showBrand?: boolean;
  showAvailability?: boolean;
  showAttributes?: boolean;
  showDocuments?: boolean;
  showGallery?: boolean;
  enableSavedLists?: boolean;
  savedListSource?: SavedListPickerSource;
  savedListScopeKey?: string;
}

function useSlugFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments.length > 0 ? decodeURIComponent(segments[segments.length - 1]!) : undefined;
}

export function ProductDetail({
  slug,
  showBrand = true,
  showAvailability = true,
  showAttributes = true,
  showDocuments = true,
  showGallery = true,
  enableSavedLists,
  savedListSource,
  savedListScopeKey,
}: ProductDetailProps) {
  const commerce = useCommerceSource();
  const listSource = savedListSource ?? (hasBuyingListsCapability(commerce) ? commerce.buyingLists : undefined);
  const listScopeKey = savedListScopeKey ?? listSource?.getScopeKey?.();
  const showSavedLists = enableSavedLists ?? Boolean(listSource && listScopeKey);
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const pathSlug = useSlugFromPath();
  const effectiveSlug = (slug ?? pathSlug ?? '').trim();

  const state = useAsyncResource(async () => {
    if (!commerce || !effectiveSlug) return null;
    return commerce.getProductBySlug(effectiveSlug);
  }, [commerce, effectiveSlug]);

  const [activeImage, setActiveImage] = useState(0);

  if (!commerce) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="ProductDetail"
            adapterName="CommerceSource"
            reason={t('commerce.adapter.reason')}
          />
        </div>
      </BlockRoot>
    );
  }

  if (!effectiveSlug || state.kind === 'empty') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            title={t('commerce.detail.notFound.title')}
            hint={t('commerce.detail.notFound.hint')}
          />
        </div>
      </BlockRoot>
    );
  }

  if (state.kind === 'loading') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 480px) 1fr', gap: 32 }}>
            <SkeletonRow
              count={1}
              layout="grid"
              minCardWidth={480}
              aspect="4 / 3"
              showText={false}
            />
            <SkeletonRow count={3} layout="list" />
          </div>
        </div>
      </BlockRoot>
    );
  }

  if (state.kind === 'error') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            title={t('commerce.detail.error.title')}
            hint={t('commerce.detail.error.hint')}
          />
        </div>
      </BlockRoot>
    );
  }

  const product = state.data;

  if (product.redirectTo) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            title={t('commerce.detail.moved.title')}
            hint={t('commerce.detail.moved.hint')}
          />
          <p style={{ marginTop: 12 }}>
            <PortalLink href={product.redirectTo}>{t('commerce.detail.moved.link')}</PortalLink>
          </p>
        </div>
      </BlockRoot>
    );
  }

  const images =
    product.images && product.images.length > 0
      ? product.images
      : product.heroImage
        ? [product.heroImage]
        : [];
  const activeImg = images[activeImage] ?? images[0];

  return (
    <BlockRoot as="section" style={sectionPad}>
      <style dangerouslySetInnerHTML={{ __html: PRODUCT_DETAIL_CSS }} />
      <div style={{ ...sectionInner }}>
        <div className="commerce-product-detail" style={detailGridStyle}>
          {showGallery && (
            <div className="commerce-product-detail__gallery" {...part('gallery')}>
              <ProductThumb
                image={activeImg}
                aspect="4 / 5"
                style={heroImageStyle}
                seed={product?.sku ?? product?.slug ?? String(product?.id ?? '')}
              />
              {images.length > 1 && (
                <div
                  role="group"
                  aria-label={t('commerce.detail.galleryLabel')}
                  style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}
                >
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-pressed={i === activeImage}
                      aria-label={t('commerce.detail.galleryThumb', {
                        index: i + 1,
                        total: images.length,
                      })}
                      onClick={() => setActiveImage(i)}
                      style={{
                        width: 56,
                        height: 56,
                        padding: 0,
                        border:
                          i === activeImage
                            ? '2px solid var(--color-brand-500, #319db3)'
                            : '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
                        borderRadius: 'var(--radius-sm, 6px)',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: 'none',
                      }}
                    >
                      <ProductThumb image={img} aspect="1 / 1" style={{ borderRadius: 0 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="commerce-product-detail__summary" {...part('summary')}>
            {showBrand && product.brandName && <div style={brandStyle}>{product.brandName}</div>}
            <h1 style={titleStyle}>{product.title}</h1>
            {product.shortDescription && <p style={shortDescStyle}>{product.shortDescription}</p>}

            <div
              style={{
                marginTop: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <ProductPrice price={product.price} listPrice={product.listPrice} size="lg" />
              {showAvailability && <AvailabilityBadge availability={product.availability} />}
            </div>

            {product.sku && (
              <div style={metaLineStyle}>
                {t('commerce.detail.sku')}: {product.sku}
              </div>
            )}
            {showSavedLists && listSource && listScopeKey && <div style={{ marginTop: 16 }}>
              <SavedListPicker key={`${listScopeKey}:${product.id}`} source={listSource} scopeKey={listScopeKey} product={{ id: String(product.id), title: product.title }} />
            </div>}

            {showAttributes && product.attributes && product.attributes.length > 0 && (
              <dl style={attrListStyle} {...part('attributes')}>
                {product.attributes.map((attr, i) => (
                  <div key={`${attr.code}-${i}`} style={attrRowStyle}>
                    <dt style={attrTermStyle}>{attr.label}</dt>
                    <dd style={attrValueStyle}>{attr.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {showDocuments && product.documents && product.documents.length > 0 && (
              <div style={{ marginTop: 24 }} {...part('documents')}>
                <h2 style={sectionHeadingStyle}>{t('commerce.detail.documents')}</h2>
                <ul style={docListStyle}>
                  {product.documents.map((doc, i) => (
                    <li key={i}>
                      <PortalLink href={doc.url} style={docLinkStyle}>
                        {doc.title}
                      </PortalLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {product.description && (
          <div style={descriptionPanelStyle} {...part('description')}>
            <h2 style={sectionHeadingStyle}>{t('commerce.detail.description')}</h2>
            <div style={descriptionStyle}>{product.description}</div>
          </div>
        )}
      </div>
    </BlockRoot>
  );
}

const detailGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.08fr) minmax(320px, .92fr)',
  gap: 'clamp(32px, 7vw, 88px)',
  alignItems: 'start',
};

const heroImageStyle: CSSProperties = {
  borderRadius: 'var(--radius-card, 12px)',
  overflow: 'hidden',
  background: 'var(--color-surface-muted, #f4f4f5)',
};

const brandStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #71717a)',
  textTransform: 'uppercase',
  letterSpacing: 0,
  fontWeight: 700,
};

const titleStyle: CSSProperties = {
  margin: '8px 0 0',
  fontFamily: 'var(--font-family-display, inherit)',
  fontSize: 'clamp(36px, 4.5vw, 64px)',
  fontWeight: 650,
  lineHeight: 1.04,
  letterSpacing: 0,
  color: 'var(--color-text-primary, #18181b)',
};

const shortDescStyle: CSSProperties = {
  margin: '18px 0 0',
  fontSize: 'var(--font-size-lg, 18px)',
  color: 'var(--color-text-secondary, #52525b)',
  lineHeight: 1.65,
};

const metaLineStyle: CSSProperties = {
  marginTop: 12,
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #71717a)',
};

const attrListStyle: CSSProperties = {
  marginTop: 30,
  paddingTop: 20,
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
};

const attrRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0',
  fontSize: 'var(--font-size-sm, 14px)',
};

const attrTermStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-tertiary, #71717a)',
};

const attrValueStyle: CSSProperties = {
  margin: 0,
  color: 'var(--color-text-primary, #18181b)',
  fontWeight: 500,
  textAlign: 'right',
};

const sectionHeadingStyle: CSSProperties = {
  margin: '0 0 12px',
  fontFamily: 'var(--font-family-display, inherit)',
  fontSize: 'clamp(22px, 3vw, 30px)',
  fontWeight: 650,
  color: 'var(--color-text-primary, #18181b)',
};

const docListStyle: CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const docLinkStyle: CSSProperties = {
  color: 'var(--color-brand-600, #287f96)',
  textDecoration: 'underline',
  fontSize: 'var(--font-size-sm, 14px)',
};

const descriptionStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  fontSize: 'var(--font-size-md, 16px)',
  lineHeight: 1.7,
  color: 'var(--color-text-primary, #18181b)',
};

const descriptionPanelStyle: CSSProperties = {
  marginTop: 'clamp(48px, 8vw, 96px)',
  paddingTop: 'clamp(28px, 4vw, 48px)',
  maxWidth: 860,
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,.08))',
};

const PRODUCT_DETAIL_CSS = `
.commerce-product-detail__summary { position: sticky; top: 112px; }
@media (max-width: 760px) {
  .commerce-product-detail { grid-template-columns: 1fr !important; }
  .commerce-product-detail__summary { position: static; }
}
`;
